import {useCallback, useEffect, useState} from 'react'
import {ChevronLeft, ChevronRight, Filter, ArrowLeft} from 'lucide-react'
import {Heading} from '@/components/tailwind/heading'
import {Text} from '@/components/tailwind/text'
import {Badge} from '@/components/tailwind/badge'
import {Button} from '@/components/tailwind/button'
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from '@/components/tailwind/table'
import {Select} from '@/components/tailwind/select'
import {Input} from '@/components/tailwind/input'
import {useAdminTranslation} from "@/i18n/hooks"
import {useAuth} from '@/context/use-auth'
import {useNavigate} from 'react-router-dom'
import LoadingSpinner from "@/components/loader-spinner"
import {getEmailSendLogs} from '@/services/email-send-log-services'
import type {EmailSendLogEntry} from '@/services/email-send-log-services'
import {formatDateTimeString} from '@/lib/utils'
import log from '@/lib/logger'

const PAGE_SIZE = 20

function getStatusBadgeColor(status: string): "green" | "red" | "zinc" {
    switch (status) {
        case 'SUCCESS':
            return 'green'
        case 'FAILURE':
            return 'red'
        default:
            return 'zinc'
    }
}

export default function AdminEmailSendLogPage() {
    const {t} = useAdminTranslation()
    const {isAdmin, token} = useAuth()
    const navigate = useNavigate()

    const [loading, setLoading] = useState(true)
    const [entries, setEntries] = useState<EmailSendLogEntry[]>([])
    const [page, setPage] = useState(0)
    const [totalElements, setTotalElements] = useState(0)
    const [totalPages, setTotalPages] = useState(0)
    const [statusFilter, setStatusFilter] = useState<string>('')
    const [searchQuery, setSearchQuery] = useState<string>('')

    useEffect(() => {
        if (!isAdmin()) {
            navigate("/dashboard")
        }
    }, [isAdmin, navigate])

    const fetchEmailSendLogs = useCallback(() => {
        if (!token) return
        setLoading(true)
        getEmailSendLogs(token, page, PAGE_SIZE)
            .then((result) => {
                if (result) {
                    setEntries(result.entries)
                    setTotalElements(result.totalElements)
                    setTotalPages(result.totalPages)
                }
            })
            .catch((err) => {
                log.error('Failed to load email send logs:', err)
                setEntries([])
            })
            .finally(() => setLoading(false))
    }, [token, page])

    useEffect(() => {
        fetchEmailSendLogs()
    }, [fetchEmailSendLogs])

    function handleStatusFilterChange(newStatus: string) {
        setStatusFilter(newStatus)
        setPage(0)
    }

    const filteredEntries = entries.filter((entry) => {
        if (statusFilter && entry.status !== statusFilter) return false
        if (searchQuery) {
            const query = searchQuery.toLowerCase()
            return (
                entry.recipientEmail.toLowerCase().includes(query) ||
                entry.subject.toLowerCase().includes(query) ||
                (entry.createdByName?.toLowerCase().includes(query))
            )
        }
        return true
    })

    if (!isAdmin()) return null

    return (
        <div>
            <div className="flex items-center gap-4 mb-6">
                <Button plain onClick={() => navigate('/admin/settings')}>
                    <ArrowLeft className="h-4 w-4" />
                    {t('adminSettings.emailSendLog.backToSettings')}
                </Button>
            </div>

            <Heading>{t('adminSettings.emailSendLog.title')}</Heading>
            <Text className="mt-1">{t('adminSettings.emailSendLog.description')}</Text>

            <div className="mt-6 flex flex-wrap items-center gap-3">
                <Filter className="h-4 w-4 text-zinc-500 dark:text-zinc-400" />
                <Select
                    value={statusFilter}
                    onChange={(e) => handleStatusFilterChange(e.target.value)}
                    className="max-w-xs"
                >
                    <option value="">{t('adminSettings.emailSendLog.filter.all')}</option>
                    <option value="SUCCESS">{t('adminSettings.emailSendLog.filter.success')}</option>
                    <option value="FAILURE">{t('adminSettings.emailSendLog.filter.failure')}</option>
                </Select>
                <Input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={t('adminSettings.emailSendLog.searchPlaceholder')}
                    className="max-w-xs"
                />
            </div>

            {loading ? (
                <div className="mt-6">
                    <LoadingSpinner />
                </div>
            ) : filteredEntries.length === 0 ? (
                <Text className="mt-6 text-zinc-500 dark:text-zinc-400">
                    {t('adminSettings.emailSendLog.noEntries')}
                </Text>
            ) : (
                <>
                    <div className="mt-4 overflow-x-auto">
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableHeader>{t('adminSettings.emailSendLog.columns.timestamp')}</TableHeader>
                                    <TableHeader>{t('adminSettings.emailSendLog.columns.user')}</TableHeader>
                                    <TableHeader>{t('adminSettings.emailSendLog.columns.recipient')}</TableHeader>
                                    <TableHeader>{t('adminSettings.emailSendLog.columns.subject')}</TableHeader>
                                    <TableHeader>{t('adminSettings.emailSendLog.columns.type')}</TableHeader>
                                    <TableHeader>{t('adminSettings.emailSendLog.columns.provider')}</TableHeader>
                                    <TableHeader>{t('adminSettings.emailSendLog.columns.status')}</TableHeader>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredEntries.map((entry) => (
                                    <TableRow key={entry.id}>
                                        <TableCell className="whitespace-nowrap text-sm">
                                            {formatDateTimeString(entry.createdAt)}
                                        </TableCell>
                                        <TableCell className="text-sm">
                                            {entry.createdByName || '-'}
                                        </TableCell>
                                        <TableCell className="text-sm">
                                            {entry.recipientEmail}
                                        </TableCell>
                                        <TableCell className="text-sm max-w-xs truncate" title={entry.subject}>
                                            {entry.subject}
                                        </TableCell>
                                        <TableCell className="text-sm">
                                            {t(`adminSettings.emailSendLog.emailType.${entry.emailType}`)}
                                        </TableCell>
                                        <TableCell className="text-sm">
                                            {entry.provider}
                                        </TableCell>
                                        <TableCell>
                                            <Badge color={getStatusBadgeColor(entry.status)}>
                                                {t(`adminSettings.emailSendLog.status.${entry.status}`)}
                                            </Badge>
                                            {entry.failureSummary && (
                                                <Text className="mt-1 text-xs text-red-600 dark:text-red-400 max-w-xs truncate" title={entry.failureSummary}>
                                                    {entry.failureSummary}
                                                </Text>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>

                    {totalPages > 1 && (
                        <div className="flex justify-between items-center mt-6">
                            <Text className="text-sm text-zinc-500 dark:text-zinc-400">
                                {t('adminSettings.emailSendLog.pagination.page', {current: page + 1, total: totalPages})}
                                {' '}({t('adminSettings.emailSendLog.pagination.entries', {count: totalElements})})
                            </Text>
                            <div className="flex gap-2">
                                <Button
                                    plain
                                    onClick={() => setPage(page - 1)}
                                    disabled={page === 0}
                                >
                                    <ChevronLeft className="h-4 w-4" />
                                    {t('adminSettings.emailSendLog.pagination.previous')}
                                </Button>
                                <Button
                                    plain
                                    onClick={() => setPage(page + 1)}
                                    disabled={page >= totalPages - 1}
                                >
                                    {t('adminSettings.emailSendLog.pagination.next')}
                                    <ChevronRight className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    )
}
